import { Fragment, useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-final-form';
import { HEAT_FEATURE_STRINGS } from 'accounts/constants/strings/heat.strings';
import Field from 'components/Field/Field';
import { InputFinalFormAdapter } from 'components/Input/Input';
import {
  SelectorFinalFormAdapter,
  SELECTOR_TYPES,
  GRID_SIZE,
} from 'components/Selector/Selector';
import { Row, Col } from 'forkedlibraries/react-bootstrap';
import FormLayout from 'layouts/FormLayout/FormLayout';
import { HeatFeatureTypes } from 'models/enums';
import { required } from 'utils/validation';

// Furnace location options
const FURNACE_LOCATION_OPTIONS = [
  { label: 'Basement', key: 'basement' },
  { label: 'Garage', key: 'garage' },
  { label: 'Closet', key: 'closet' },
  { label: 'Attic', key: 'attic' },
  { label: 'Crawl Space', key: 'crawl-space' },
  { label: 'Outside In Front', key: 'outside-in-front' },
  { label: 'Outside In Back', key: 'outside-in-back' },
  { label: 'Outside On The Side', key: 'outside-on-the-side' },
  { label: 'Utility / Mechanical Room', key: 'utility-mechanical-room' },
  { label: 'Other', key: 'other' },
];

interface HeatFormFieldsProps {
  name?: string;
  disabled?: boolean;
}

export function HeatFormFields({
  name = '',
  disabled = false,
}: HeatFormFieldsProps) {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const form = useForm();

  // Smart matching function to find predefined options
  const findFurnaceLocationMatchingOption = useCallback((value: string) => {
    if (!value) return null;

    const normalizedValue = value.trim().toLowerCase();

    // First try exact match
    const exactMatch = FURNACE_LOCATION_OPTIONS.find(
      (option) => option.key === value,
    );
    if (exactMatch) return exactMatch;

    // Then try case-insensitive match
    const caseInsensitiveMatch = FURNACE_LOCATION_OPTIONS.find(
      (option) => option.key.toLowerCase() === normalizedValue,
    );
    if (caseInsensitiveMatch) return caseInsensitiveMatch;

    // Try partial matches for common variations (enhanced matching)
    const partialMatch = FURNACE_LOCATION_OPTIONS.find((option) => {
      const optionNormalized = option.key
        .toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove punctuation
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
      const valueNormalized = normalizedValue
        .replace(/[^\w\s]/g, '') // Remove punctuation
        .replace(/\s+/g, ' ') // Normalize spaces
        .trim();
      // Check various matching patterns
      return (
        optionNormalized.includes(valueNormalized) ||
        valueNormalized.includes(optionNormalized) ||
        optionNormalized.replace(/\s/g, '') ===
          valueNormalized.replace(/\s/g, '') ||
        // Split by spaces and check if major words match (3+ characters)
        (optionNormalized
          .split(' ')
          .some((word) => word.length > 2 && valueNormalized.includes(word)) &&
          valueNormalized
            .split(' ')
            .some((word) => word.length > 2 && optionNormalized.includes(word)))
      );
    });
    if (partialMatch) return partialMatch;

    return null;
  }, []);

  // Watch for initial form value and set selectedLocation accordingly
  useEffect(() => {
    const currentValue = form.getFieldState(
      `${name}.attributes.furnaceLocation`,
    )?.value;
    if (currentValue && selectedLocation === null) {
      const matchingOption = findFurnaceLocationMatchingOption(currentValue);
      if (matchingOption) {
        setSelectedLocation(matchingOption.key);
      } else {
        // Custom value exists, select "Other"
        setSelectedLocation('other');
      }
    }
  }, [form, name, selectedLocation, findFurnaceLocationMatchingOption]);

  // Handle smart conversion when user leaves the input field (onBlur)
  const handleOtherInputBlur = useCallback(
    (input: any) => (event: any) => {
      const { value } = event.target;
      const matchingOption = findFurnaceLocationMatchingOption(value);
      if (matchingOption) {
        // User typed something that matches a predefined option, convert it
        setSelectedLocation(matchingOption.key);
        input.onChange(matchingOption.key);
      }
      // Call original onBlur if it exists
      if (input.onBlur) {
        input.onBlur(event);
      }
    },
    [findFurnaceLocationMatchingOption],
  );

  const renderFurnaceLocationField = useCallback(
    ({ input, meta }) => {
      const showTextInput = selectedLocation === 'other';

      return (
        <>
          <SelectorFinalFormAdapter
            input={{
              value: selectedLocation,
              onChange: (value: any) => {
                setSelectedLocation(value);
                if (value !== 'other') {
                  // Set furnaceLocation to the selected predefined option
                  input.onChange(value);
                } else {
                  // Clear the form value when "Other" is selected so user starts with empty text input
                  input.onChange('');
                }
              },
            }}
            meta={{}}
            ignoreError
            onChangeCustom={() => {}}
            customCallback={() => {}}
            type={SELECTOR_TYPES.SMALLTEXTBUTTON}
            buttons={FURNACE_LOCATION_OPTIONS}
            disabled={disabled}
            grid
            gridSize={GRID_SIZE.EXTRA_SMALL}
          />

          {showTextInput && (
            <div className="mt-lg">
              <InputFinalFormAdapter
                input={{
                  ...input,
                  onBlur: handleOtherInputBlur(input),
                }}
                meta={meta}
                ignoreError={false}
                onChangeCustom={() => {}}
                newFieldComponent={false}
                placeholder={HEAT_FEATURE_STRINGS.inputPlaceholder}
                disabled={disabled}
                applyDisabledStyle={disabled}
              />
            </div>
          )}
        </>
      );
    },
    [selectedLocation, disabled, handleOtherInputBlur],
  );

  return (
    <Fragment>
      <FormLayout.Section
        firstSection
        sectionTitle={HEAT_FEATURE_STRINGS.heatherTitle}
        separatorType="none"
      >
        <Row>
          <Col md={12}>
            <Field
              name={`${name}.attributes.heatType`}
              component={SelectorFinalFormAdapter}
              fluid
              validate={required}
              type={SELECTOR_TYPES.SMALLTEXTBUTTON}
              className="mb-lg"
              buttons={[
                {
                  label: HeatFeatureTypes.Electric,
                  key: HeatFeatureTypes.Electric,
                },
                {
                  label: HeatFeatureTypes.Gas,
                  key: HeatFeatureTypes.Gas,
                },
                {
                  label: HeatFeatureTypes.Oil,
                  key: HeatFeatureTypes.Oil,
                },
              ]}
              disabled={disabled}
            />
          </Col>
        </Row>
      </FormLayout.Section>

      <FormLayout.Section
        sectionTitle={HEAT_FEATURE_STRINGS.furnaceLocationTitle}
        separatorType="none"
        className="mt-sm"
      >
        <Field
          name={`${name}.attributes.furnaceLocation`}
          validate={required}
          component={renderFurnaceLocationField}
        />
      </FormLayout.Section>
    </Fragment>
  );
}
